import React, { useState } from 'react';
import { Add as AddIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { CONTACT_INFO } from '../../constants';
import { useRevealOnScroll } from '../../hooks/useRevealOnScroll';

export interface FaqItem {
  id: string;
  question: string;
  answer: string;
}

/** Ordre pensé pour la grille 2 colonnes (gauche / droite, ligne par ligne). */
const defaultFaqItems: FaqItem[] = [
  {
    id: 'qu-est-ce-que-promea',
    question: 'Qu’est-ce que Proméa ?',
    answer:
      'Proméa est un promoteur et aménageur immobilier basé en Bretagne Sud. Nous concevons et commercialisons des terrains à bâtir, logements collectifs, bureaux et locaux d’activités, en accompagnant les acheteurs à chaque étape.',
  },
  {
    id: 'comment-acheter',
    question: 'Comment acheter un bien neuf avec Proméa ?',
    answer:
      'Parcourez nos programmes en ligne, filtrez par ville ou par type de bien, puis prenez contact avec un conseiller. Nous organisons des visites, vous aidons à monter votre dossier de financement et assurons le suivi jusqu’à la livraison.',
  },
  {
    id: 'types-de-biens',
    question: 'Quels types de biens proposez-vous ?',
    answer:
      'Nous proposons des terrains à bâtir, des appartements et maisons en programmes collectifs, des bureaux et des locaux d’activités. Chaque projet est présenté avec ses lots disponibles, surfaces et prix indicatifs.',
  },
  {
    id: 'ou-sont-les-projets',
    question: 'Où se situent vos projets ?',
    answer:
      'Nos opérations sont principalement situées en Bretagne Sud (Morbihan, Finistère sud, etc.). La localisation précise de chaque programme est indiquée sur sa fiche projet et dans nos espaces de vente.',
  },
  {
    id: 'visite',
    question: 'Comment prendre rendez-vous pour une visite ?',
    answer:
      'Vous pouvez nous contacter par téléphone, via le formulaire du site ou en vous rendant dans l’un de nos espaces de vente. Un conseiller vous proposera un créneau adapté pour découvrir le programme ou le lot qui vous intéresse.',
  },
  {
    id: 'tva-reduite',
    question: 'Puis-je bénéficier de la TVA réduite ou du PTZ ?',
    answer:
      'Selon la localisation et la nature du bien, certains programmes peuvent ouvrir droit à des dispositifs d’aide (TVA réduite, PTZ, etc.). Nos équipes vous indiquent les conditions applicables au moment de votre projet.',
  },
  {
    id: 'vendre-terrain',
    question: 'Comment vendre mon terrain à Proméa ?',
    answer:
      'Rendez-vous sur la page « Vendre à Proméa » pour déposer votre demande ou prendre rendez-vous. Nous étudions la faisabilité de votre foncier et vous recontactons avec une proposition adaptée.',
  },
  {
    id: 'etapes-achat',
    question: 'Quelles sont les étapes entre la réservation et la livraison ?',
    answer:
      'Après la réservation et la signature du contrat de vente, vous suivez l’avancement du chantier via notre suivi de programme. La livraison intervient selon le calendrier prévu, avec une visite de réception et la remise des clés.',
  },
  {
    id: 'donnees-securisees',
    question: 'Mes données personnelles sont-elles protégées ?',
    answer:
      'Oui. Les informations transmises via nos formulaires sont traitées de manière confidentielle, conformément à la réglementation en vigueur. Elles servent uniquement à répondre à votre demande et à vous accompagner dans votre projet.',
  },
  {
    id: 'decouvrir-programmes',
    question: 'Prêt à découvrir nos programmes ?',
    answer:
      'Consultez la page « Acheter » pour explorer l’ensemble de nos offres actuelles, ou contactez-nous pour un conseil personnalisé. Nos équipes sont disponibles du lundi au vendredi pour vous guider.',
  },
];

interface FaqSectionProps {
  items?: FaqItem[];
  className?: string;
  sectionId?: string;
  eyebrowLabel?: string;
}

const FaqSection: React.FC<FaqSectionProps> = ({
  items = defaultFaqItems,
  className = '',
  sectionId,
  eyebrowLabel = '07 • FAQ',
}) => {
  const [openId, setOpenId] = useState<string | null>(null);
  const { ref: headerRef, visible: headerVisible } = useRevealOnScroll<HTMLElement>();
  const { ref: gridRef, visible: gridVisible } = useRevealOnScroll<HTMLDivElement>();

  const toggle = (id: string) => {
    setOpenId((current) => (current === id ? null : id));
  };

  return (
    <section
      {...(sectionId ? { id: sectionId } : {})}
      className={`faq-section relative scroll-mt-28 overflow-hidden py-12 md:scroll-mt-32 md:py-14 lg:py-16 ${className}`}
      aria-labelledby="faq-heading"
    >
      <div className="relative z-[1] mx-auto w-full max-w-[1440px] px-4 sm:px-5 md:px-6">
        <header
          ref={headerRef}
          className={`reveal-up mb-10 max-w-2xl md:mb-12 lg:mb-14 ${headerVisible ? 'reveal-up--visible' : ''}`}
        >
          <p className="eyebrow-accent mb-3 tracking-[0.18em]">{eyebrowLabel}</p>
          <div
            className={`accent-dash reveal-line ${headerVisible ? 'reveal-line--visible' : ''}`}
            aria-hidden
          />
          <h2 id="faq-heading" className="headline-brand-section mt-6 max-w-2xl text-[var(--color-text)]">
            Questions <span className="headline-brand-accent">fréquentes</span>
          </h2>
          <p className="faq-section-lead mt-4 max-w-xl text-[15px] font-normal leading-[1.7] sm:text-base lg:text-[17px]">
            Besoin d&apos;un renseignement complémentaire ?
          </p>
          <p className="faq-section-lead mt-1 max-w-xl text-[15px] font-normal leading-[1.7] sm:text-base lg:text-[17px]">
            Contactez nos conseillers ou appelez-nous au{' '}
            <a
              href={`tel:${CONTACT_INFO.phone.replace(/[^+0-9]/g, '')}`}
              className="font-medium text-orange-500 transition-colors hover:text-orange-400"
            >
              {CONTACT_INFO.phone}
            </a>
            .
          </p>
        </header>

        <div
          ref={gridRef}
          className={`reveal-stagger grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4 ${gridVisible ? 'reveal-stagger--active' : ''}`}
        >
          {items.map((item) => {
            const isOpen = openId === item.id;

            return (
              <article
                key={item.id}
                className={`faq-item overflow-hidden ${isOpen ? 'faq-item--open' : ''}`}
              >
                <button
                  type="button"
                  id={`faq-trigger-${item.id}`}
                  aria-expanded={isOpen}
                  aria-controls={`faq-panel-${item.id}`}
                  onClick={() => toggle(item.id)}
                  className="faq-item__trigger"
                >
                  <span className="faq-item__plus" aria-hidden>
                    <AddIcon className="h-[0.95rem] w-[0.95rem]" />
                  </span>
                  <span className="faq-item__question">{item.question}</span>
                  <ExpandMoreIcon
                    className={`faq-item__chevron ${isOpen ? 'faq-item__chevron--open' : ''}`}
                    aria-hidden
                  />
                </button>

                <div
                  id={`faq-panel-${item.id}`}
                  role="region"
                  aria-labelledby={`faq-trigger-${item.id}`}
                  hidden={!isOpen}
                  className={isOpen ? 'block' : 'hidden'}
                >
                  <div className="faq-item__answer">
                    <p>{item.answer}</p>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FaqSection;
